
import React, { useRef, useState, useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import { Mesh, Euler, Group, Vector3 } from 'three';

interface NeedleProps {
  position: [number, number, number];
  rotation: [number, number, number];
  scale: number;
}

const Sprig: React.FC<NeedleProps> = ({ position, rotation, scale }) => {
  const groupRef = useRef<Group>(null);
  const [hovered, setHovered] = useState(false);
  const initialRotation = useMemo(() => new Euler(...rotation), [rotation]);
  const currentRotation = useRef(new Euler(...rotation));
  const velocity = useRef(new Vector3(0, 0, 0));

  useFrame((state, delta) => {
    if (!groupRef.current) return;

    if (hovered) {
      // 当鼠标悬停时，产生一个随机的微小推力
      velocity.current.x += (Math.random() - 0.5) * 0.2;
      velocity.current.z += (Math.random() - 0.5) * 0.2;
    }

    // 弹性回复力逻辑
    velocity.current.x += (initialRotation.x - currentRotation.current.x) * 0.1;
    velocity.current.z += (initialRotation.z - currentRotation.current.z) * 0.1;
    
    // 阻尼
    velocity.current.multiplyScalar(0.92);

    currentRotation.current.x += velocity.current.x;
    currentRotation.current.z += velocity.current.z;

    groupRef.current.rotation.x = currentRotation.current.x;
    groupRef.current.rotation.z = currentRotation.current.z;
    
    // 轻微的上下浮动
    groupRef.current.position.y = position[1] + Math.sin(state.clock.elapsedTime * 0.8 + position[0]) * 0.02;
  });

  const needleCluster = useMemo(() => {
    return [0, 1, 2].map((i) => ({
      rotation: [
        (Math.random() - 0.5) * 0.3,
        (i * Math.PI * 2) / 3 + (Math.random() - 0.5) * 0.1,
        0.5 + Math.random() * 0.3
      ] as [number, number, number],
      scale: 0.9 + Math.random() * 0.5,
      color: i % 2 === 0 ? "#115e59" : "#134e4a" 
    }));
  }, []);

  return (
    <group
      ref={groupRef}
      position={position}
      scale={scale}
      onPointerOver={(e) => {
        e.stopPropagation();
        setHovered(true);
      }}
      onPointerOut={() => setHovered(false)}
    >
      {needleCluster.map((needle, idx) => (
        <mesh key={idx} rotation={needle.rotation} scale={[1, needle.scale, 1]} castShadow>
          <boxGeometry args={[0.015, 0.45, 0.03]} />
          <meshStandardMaterial 
            color={hovered ? "#2dd4bf" : needle.color} 
            emissive={hovered ? "#14b8a6" : "#042f2e"}
            emissiveIntensity={hovered ? 1.5 : 0.2}
            roughness={0.6}
          />
        </mesh>
      ))}
      {/* 隐藏的碰撞球体，增大交互判定范围 */}
      <mesh visible={false}>
        <sphereGeometry args={[0.25, 6, 6]} />
      </mesh>
    </group>
  );
};

export const FoliageGroup: React.FC<{ level: number, radius: number, height: number, baseHeight: number }> = ({ level, radius, height, baseHeight }) => {
  const sprigCount = 80 + level * 40; 
  const sprigs = useMemo(() => {
    const arr = [];
    for (let i = 0; i < sprigCount; i++) {
      const angle = i * 1.618 * Math.PI * 2;
      const r = radius * (0.35 + Math.random() * 0.65);
      const px = Math.cos(angle) * r;
      const pz = Math.sin(angle) * r;
      // 这里的 Y 坐标会随层级动态调整
      const py = baseHeight + (Math.random() * height * 0.85);
      
      arr.push({
        position: [px, py, pz] as [number, number, number],
        rotation: [
          -0.3 - Math.random() * 0.2, 
          angle, 
          0
        ] as [number, number, number],
        scale: 0.8 + Math.random() * 1.2
      });
    }
    return arr;
  }, [sprigCount, radius, height, baseHeight]);

  return (
    <group>
      {sprigs.map((sprig, idx) => (
        <Sprig key={idx} {...sprig} />
      ))}
    </group>
  );
};
